import React, { useRef, useState } from 'react';
import { Alert, Button, Input, Modal, Tooltip, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { copyPricingTemplate } from '../../services/Apis';

interface Props {
    iconOnly?: boolean;
    templateId: string;
    templateName: string;
    onCopied: (template: { template_id: number; template_name: string }) => void;
    onTokenExpired: () => void;
}

const CopyPricingTemplateButton: React.FC<Props> = ({ templateId, templateName, onCopied, onTokenExpired, iconOnly = false }) => {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [error, setError] = useState('');
    const [copying, setCopying] = useState(false);
    const inFlight = useRef(false);
    const copy = async () => {
        if (inFlight.current) return;
        if (!name.trim()) { setError('Enter a name for the new template.'); return; }
        inFlight.current = true;
        setCopying(true);
        setError('');
        try {
            const template = await copyPricingTemplate(templateId, name.trim());
            setOpen(false);
            message.success('Template cloned. You can now edit the new template.');
            onCopied(template);
        } catch (failure: any) {
            if (failure?.response?.status === 401) {
                setOpen(false);
                onTokenExpired();
            } else {
                const detail = failure?.response?.data?.detail;
                setError(typeof detail === 'string' ? detail : 'Unable to clone the template. Please try again.');
            }
        } finally {
            inFlight.current = false;
            setCopying(false);
        }
    };
    return <>
        <Tooltip title="Clone template">
            <Button aria-label="Clone Template" icon={<CopyOutlined />}
                type={iconOnly ? 'text' : 'default'} size={iconOnly ? 'small' : 'middle'}
                style={iconOnly ? { color: '#4096ff' } : undefined} onClick={() => {
                setName(`${templateName.slice(0, 247)} (Clone)`); setError(''); setOpen(true);
            }}>{iconOnly ? null : 'Clone Template'}</Button>
        </Tooltip>
        <Modal title="Clone pricing template" open={open} okText="Create clone" confirmLoading={copying}
            okButtonProps={{ 'aria-label': 'Create clone' }} cancelButtonProps={{ disabled: copying }}
            closable={!copying} maskClosable={!copying} keyboard={!copying}
            onOk={copy} onCancel={() => { if (!inFlight.current) setOpen(false); }}>
            <p>Clone the saved version of “{templateName}” into a new editable template. The clone does not include deal links or unsaved changes.</p>
            <label htmlFor={`copy-template-name-${templateId}`}>New template name</label>
            <Input id={`copy-template-name-${templateId}`} value={name} maxLength={255} disabled={copying}
                onChange={event => setName(event.target.value)} onPressEnter={copy} />
            {error && <Alert type="error" showIcon message={error} style={{ marginTop: 12 }} />}
        </Modal>
    </>;
};

export default CopyPricingTemplateButton;
